import { Business, People } from '@mui/icons-material';
import CategoryIcon from '@mui/icons-material/Category';
import DateRangeIcon from '@mui/icons-material/DateRange';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PublicIcon from '@mui/icons-material/Public';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import WebIcon from '@mui/icons-material/Web';
import { SvgIcon } from '@mui/material';
import type { Search } from '~/types';
export interface Column {
  id: keyof Search.Site;
  name: string;
  numeric: boolean;
  sortable: boolean;
  icon: typeof SvgIcon;
  description: string;
}

export const domain: Column = {
  id: 'domain',
  numeric: false,
  description: 'The registered domain name (i.e. "nerdydata.com").',
  icon: WebIcon,
  name: 'Domain',
  sortable: false,
};

export const tld: Column = {
  id: 'tld',
  numeric: false,
  name: 'Domain Suffix (TLD)',
  sortable: true,
  icon: PublicIcon,
  description: 'Top level domain of the website (i.e. .com, .org, .net, etc.)',
};

export const url: Column = {
  id: 'url',
  numeric: false,
  name: 'URL',
  sortable: true,
  icon: WebIcon,
  description: 'The full URL on which the search results were found.',
};

export const firstSeenAt: Column = {
  id: 'first_seen_at',
  name: 'First Crawl Date',
  description: 'The date our crawlers first indexed this domain.',
  sortable: true,
  numeric: false,
  icon: DateRangeIcon,
};

export const updatedAt: Column = {
  id: 'updated_at',
  numeric: true,
  name: 'Last Crawled',
  sortable: true,
  icon: DateRangeIcon,
  description: 'Most recent date this website was successfully crawled',
};

export const rank: Column = {
  id: 'rank',
  numeric: true,
  name: 'Page Rank',
  sortable: true,
  icon: StarHalfIcon,
  description:
    "A page popularity ranking based on a domain's backlinks. Smaller numbers signal more popular domains.",
};

export const spendMin: Column = {
  id: 'spend_min',
  numeric: true,
  name: 'Tech Spend',
  sortable: true,
  icon: MonetizationOnIcon,
  description:
    'The estimated minimum and maximum annual tech spend each webpage has, based on the free, freemium, and paid plans of their tech stack.',
};

export const spendMax: Column = {
  id: 'spend_max',
  numeric: true,
  name: 'Tech Spend',
  sortable: true,
  icon: MonetizationOnIcon,
  description:
    'The estimated minimum and maximum annual tech spend each webpage has, based on the free, freemium, and paid plans of their tech stack.',
};

export const vertical: Column = {
  id: 'vertical',
  numeric: false,
  name: 'Verticals',
  sortable: true,
  icon: CategoryIcon,
  description: 'Businesses that are in a certain vertical',
};

export const industries: Column = {
  id: 'industries',
  numeric: false,
  name: 'Industries',
  sortable: true,
  icon: CategoryIcon,
  description: 'The company name associated with this domain.',
};

export const employees: Column = {
  id: 'employees',
  name: 'Employees',
  description: "Employee count for this domain's company.",
  numeric: true,
  sortable: true,
  icon: People,
};

export const company: Column = {
  id: 'company',
  numeric: false,
  name: 'Company',
  sortable: true,
  icon: Business,
  description: 'The company that owns the website',
};

export const country: Column = {
  id: 'country',
  numeric: false,
  name: 'Countries',
  sortable: true,
  icon: PublicIcon,
  description: 'Businesses that are located in a certain country',
};

export const state: Column = {
  id: 'state',
  name: 'State/Region',
  description: "The state or region for this domain's company.",
  numeric: false,
  sortable: true,
  icon: PublicIcon,
};

export const linkedin: Column = {
  id: 'linkedin',
  name: 'Linkedin URL',
  description: 'A list of LinkedIn pages found on this domain.',
  numeric: false,
  sortable: true,
  icon: PublicIcon,
};

export const twitter: Column = {
  id: 'twitter',
  name: 'Twitter URL',
  description: 'A list of Twitter pages found on this domain.',
  numeric: false,
  sortable: true,
  icon: PublicIcon,
};

export const facebook: Column = {
  id: 'facebook',
  name: 'Facebook URL',
  description: 'A list of Facebook pages found on this domain.',
  numeric: false,
  sortable: true,
  icon: PublicIcon,
};

export const instagram: Column = {
  id: 'instagram',
  name: 'Instagram URL',
  description: 'A list of Instagram pages found on this domain.',
  numeric: false,
  sortable: true,
  icon: PublicIcon,
};

export const downloadColumns: Column[] = [
  domain,
  tld,
  url,
  firstSeenAt,
  updatedAt,
  rank,
  spendMin,
  spendMax,
  vertical,
  industries,
  employees,
  company,
  country,
  state,
  linkedin,
  twitter,
  instagram,
  facebook,
];

export const searchResultsColumns: Column[] = downloadColumns.filter(
  (field) => {
    return ['domain', 'spend_min', 'rank', 'updated_at'].includes(field.id);
  }
);
